<script lang="ts" setup>
import { UiBtn, UiScrollWrap } from '@shared/ui';
import { twJoin, twMerge } from 'tailwind-merge';
import type { UiModalProps } from './types';
import useFocusTrap from './composables/useFocusTrap';

const {
  isOpen = false,
  size = 'sm',
  class: propsClass = '',
  noPadding = false,
} = defineProps<UiModalProps>();

const { trapRef } = useFocusTrap();

const isLocked = useScrollLock(document.body, isOpen);

const modal = ref<HTMLElement | null>(null);

const sizeStylesMap = new Map<UiModalProps['size'], string>([
  ['sm', 'max-w-[508px]'],
  ['md', 'max-w-[778px]'],
  ['lg', 'max-w-[1046px]'],
]);

const rootClass = computed(() => twMerge(twJoin('fixed z-99 inset-0 w-full h-full flex flex-col items-center justify-center bg-gray-950/75 backdrop-blur-sm'), propsClass));

const wrapClass = computed(() => twMerge(twJoin('relative h-full w-full flex flex-col  rounded-3xl overflow-hidden'), !noPadding ? 'bg-white pt-6' : '', sizeStylesMap.get(size)));

const emit = defineEmits(['close']);

watch(() => isOpen, () => {
  isLocked.value = isOpen;
});

const closeModal = () => {
  emit('close');
};

onClickOutside(modal, closeModal);

onUnmounted(() => {
  isLocked.value = false;
});
</script>

<template>
  <Transition name="fade">
    <article
      v-if="isOpen"
      ref="trapRef"
      aria-modal="true"
      :class="rootClass"
      @keydown.esc="closeModal"
    >
      <div
        :class="['max-h-full w-full relative flex items-center justify-center', { 'p-2.5': !noPadding }]"
      >
        <div
          ref="modal"
          :class="wrapClass"
        >
          <UiBtn
            type="icon"
            icon="close"
            size="lg"
            color="muted"
            alt="Закрыть окно"
            class="absolute z-10 top-2 right-2"
            @click="closeModal"
          />
          <header
            v-if="$slots.header"
            class="py-2 px-8 max-[544px]:px-4"
          >
            <slot name="header" />
          </header>
          <main
            :class="['w-full overflow-auto', { 'pt-2': !noPadding }]"
          >
            <UiScrollWrap :class="{ 'pb-8 px-8 max-[544px]:px-4 max-[544px]:pb-4': !noPadding }">
              <slot />
            </UiScrollWrap>
          </main>
          <footer
            v-if="$slots.footer"
            class="pb-8 max-[544px]:pb-4 px-8 max-[544px]:px-4"
          >
            <slot name="footer" />
          </footer>
        </div>
      </div>
    </article>
  </Transition>
</template>
